import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteBankAssetDocument,
    type DeleteBankAssetMutation,
    type DeleteBankAssetMutationVariables,
} from '../../../../api/mutations/deleteBankAsset';
import {
    UploadBankAssetDocument,
    type UploadBankAssetMutation,
    type UploadBankAssetMutationVariables,
} from '../../../../api/mutations/uploadBankAsset';
import { BankAsset, type EnbdBankIntegrationSettings } from '../../../../api/types';

type EnbdBankIntegrationValues = EnbdBankIntegrationSettings & {
    stampRight?: File | UploadFileWithPreviewFormDataFragment;
    stampBottom?: File | UploadFileWithPreviewFormDataFragment;
};

const useHandleEnbdAssets = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        (bankId: string, values: EnbdBankIntegrationValues, initialValues?: EnbdBankIntegrationValues) => {
            const { stampRight, stampBottom } = values;
            const uploads = [];

            if (stampRight instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadBankAssetMutation, UploadBankAssetMutationVariables>({
                        mutation: UploadBankAssetDocument,
                        variables: { bankId, type: BankAsset.EnbdStampRight, upload: stampRight },
                    })
                );
            } else if (stampRight === null && isObject(initialValues.stampRight)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteBankAssetMutation, DeleteBankAssetMutationVariables>({
                        mutation: DeleteBankAssetDocument,
                        variables: { bankId, type: BankAsset.EnbdStampRight },
                    })
                );
            }

            if (stampBottom instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadBankAssetMutation, UploadBankAssetMutationVariables>({
                        mutation: UploadBankAssetDocument,
                        variables: { bankId, type: BankAsset.EnbdStampBottom, upload: stampBottom },
                    })
                );
            } else if (stampBottom === null && isObject(initialValues.stampBottom)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteBankAssetMutation, DeleteBankAssetMutationVariables>({
                        mutation: DeleteBankAssetDocument,
                        variables: { bankId, type: BankAsset.EnbdStampBottom },
                    })
                );
            }

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [apolloClient]
    );
};

export default useHandleEnbdAssets;
